import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import TopBar from "@/components/TopBar";
import BackButton from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { CATEGORIES } from "@/lib/constants";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function Onboarding() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [category, setCategory] = useState("");
  const [city, setCity] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [parentName, setParentName] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const canSubmit = category && ageGroup && (
    ageGroup === "18_plus" ? consent :
    ageGroup === "13_17"   ? consent :
    ageGroup === "under_13" ? (parentName.trim().length > 1 && consent) : false
  );

  const submit = async () => {
    if (!category) return toast.error("Pick your primary talent");
    if (!ageGroup) return toast.error("Select your age group");
    if (!consent) return toast.error("Please confirm the consent checkbox");
    setBusy(true);
    try {
      await api.post("/users/me/consent", {
        age_group: ageGroup,
        parent_guardian_name: ageGroup === "under_13" ? parentName.trim() : null,
        consent_accepted: consent,
      });
      await api.put("/users/me", { category, city });
      await refresh();
      nav("/app");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen">
      <TopBar subtitle="Get started"/>
      <div className="max-w-md mx-auto px-4 pt-6 pb-16">
        <BackButton fallback="/app" label="Back" className="mb-4"/>
        <h1 className="font-display text-3xl font-bold">A few quick steps.</h1>
        <p className="text-zinc-400 text-sm mt-2">Tell us your craft + your age group so we keep NextScene safe and compliant.</p>

        <div className="mt-6 space-y-4">
          <div>
            <Label className="text-zinc-300 text-xs">Primary talent</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="onboard-category" className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue placeholder="Choose"/></SelectTrigger>
              <SelectContent className="bg-[#180D0D] border-zinc-800 text-white max-h-64">
                {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">City (optional)</Label>
            <Input data-testid="onboard-city" value={city} onChange={(e) => setCity(e.target.value)} className="mt-1 bg-zinc-900 border-zinc-800 focus-visible:ring-[#DC2626]"/>
          </div>

          {/* Age gate — mandatory */}
          <div>
            <Label className="text-zinc-300 text-xs flex items-center gap-1"><ShieldCheck size={12} className="text-[#F5C462]"/> Your age group</Label>
            <Select value={ageGroup} onValueChange={(v) => { setAgeGroup(v); setConsent(false); setParentName(""); }}>
              <SelectTrigger data-testid="onboard-age" className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue placeholder="Select age band"/></SelectTrigger>
              <SelectContent position="popper" side="bottom" align="start" className="bg-[#180D0D] border-zinc-800 text-white z-[60]">
                <SelectItem value="under_13" data-testid="age-opt-under-13">Under 13</SelectItem>
                <SelectItem value="13_17" data-testid="age-opt-13-17">13 to 17</SelectItem>
                <SelectItem value="18_plus" data-testid="age-opt-18-plus">18 and above</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {ageGroup === "under_13" && (
            <div data-testid="under-13-block" className="rounded-2xl border border-[#F5C462]/30 bg-[#F5C462]/5 p-3 space-y-3">
              <p className="text-xs text-zinc-300 leading-relaxed">
                My parent/guardian is managing this account.
              </p>
              <div>
                <Label className="text-zinc-300 text-xs">Parent / Guardian Name</Label>
                <Input data-testid="parent-name" value={parentName} onChange={(e) => setParentName(e.target.value)} placeholder="Full name" className="mt-1 bg-zinc-900 border-zinc-800"/>
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input data-testid="consent-under13" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#DC2626] w-4 h-4"/>
                <span className="text-[11px] text-zinc-300 leading-relaxed">
                  I confirm I am the parent/guardian and agree to the Terms of Service on behalf of my child.
                </span>
              </label>
            </div>
          )}

          {ageGroup === "13_17" && (
            <div data-testid="teen-block" className="rounded-2xl border border-[#F5C462]/30 bg-[#F5C462]/5 p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input data-testid="consent-teen" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#DC2626] w-4 h-4"/>
                <span className="text-[11px] text-zinc-300 leading-relaxed">
                  I have my parent/guardian's permission to use NextScene and accept the Terms of Service.
                </span>
              </label>
            </div>
          )}

          {ageGroup === "18_plus" && (
            <div data-testid="adult-block" className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input data-testid="consent-adult" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#DC2626] w-4 h-4"/>
                <span className="text-[11px] text-zinc-300 leading-relaxed">
                  I am 18 or older and accept the Terms of Service and Privacy Policy.
                </span>
              </label>
            </div>
          )}

          <Button data-testid="onboard-submit" disabled={busy || !canSubmit} onClick={submit} className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-black font-semibold disabled:opacity-40">
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}
